import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { DollarSign, TrendingUp, Package, AlertTriangle, ArrowRight, Calendar } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreatorPayoutsPageWorking = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState({ total_sales: 0, gross_revenue: 0, units_sold: 0 });
  const [perPortfolio, setPerPortfolio] = useState([]);
  // P11.0 individual payouts
  const [recentPayouts, setRecentPayouts] = useState([]);
  // P11.0 cycle data
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState('');
  const [cycleFinancials, setCycleFinancials] = useState([]);
  const [cycleTotals, setCycleTotals] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/creator/performance`, { headers }),
      axios.get(`${API}/creator/payout-cycles`, { headers }).catch(() => ({ data: { cycles: [] } })),
      axios.get(`${API}/creator/payouts`, { headers }).catch(() => ({ data: { upcoming: [], history: [] } })),
    ]).then(([perf, summary, payoutsRes]) => {
      setTotals(perf.data.totals);
      setPerPortfolio(perf.data.per_portfolio || []);
      const cyc = summary.data.cycles || [];
      setCycles(cyc);
      if (cyc.length > 0) setSelectedCycle(cyc[0].id);
      const all = [
        ...(payoutsRes.data?.upcoming || []),
        ...(payoutsRes.data?.history || []),
      ].slice(0, 25);
      setRecentPayouts(all);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedCycle) return;
    const token = localStorage.getItem('token');
    axios.get(`${API}/creator/payout-cycles/${selectedCycle}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        setCycleFinancials(r.data.portfolio_financials || []);
        const t = (r.data.portfolio_financials || []).reduce((acc, f) => ({
          gross_revenue: acc.gross_revenue + (f.gross_revenue || 0),
          kickback_fund_usd: acc.kickback_fund_usd + (f.kickback_fund_usd || 0),
          net_revenue_usd: acc.net_revenue_usd + (f.net_to_creator_usd || 0),
          units_sold: acc.units_sold + (f.units_sold || 0),
        }), { gross_revenue: 0, kickback_fund_usd: 0, net_revenue_usd: 0, units_sold: 0 });
        setCycleTotals(t);
      });
  }, [selectedCycle]);

  return (
    <div data-testid="creator-payouts-page" style={{ padding: '40px', maxWidth: '1000px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '28px', fontWeight: 700, color: theme.colors.text.primary, fontFamily: theme.fonts.heading, marginBottom: '8px' }}>Payouts & Performance</h1>
      <p style={{ fontSize: '15px', color: theme.colors.text.secondary, marginBottom: '28px' }}>Real-time sales performance across your portfolios</p>

      {/* Payout Engine Banner */}
      <div data-testid="payout-engine-banner" style={{
        display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 20px', marginBottom: '28px',
        background: cycles.length ? '#D1FAE5' : '#FEF3C7', border: `1px solid ${cycles.length ? '#10B981' : '#F6D860'}`, borderRadius: theme.radius.md,
      }}>
        <AlertTriangle size={18} color={cycles.length ? '#059669' : '#D97706'} />
        <div>
          <p style={{ fontSize: '14px', fontWeight: 600, color: cycles.length ? '#065F46' : '#92400E' }}>
            {cycles.length ? `Payout engine active · ${cycles.length} cycle${cycles.length === 1 ? '' : 's'}` : 'Payout engine: no active cycles yet'}
          </p>
          <p style={{ fontSize: '13px', color: cycles.length ? '#065F46' : '#92400E', opacity: 0.8 }}>
            {cycles.length ? 'Real cycles below. Select a cycle to drill into per-portfolio financials.' : 'Once Admin creates and publishes a cycle, your numbers will appear here.'}
          </p>
        </div>
      </div>

      {/* P11.0: Cycle selector + financials */}
      {cycles.length > 0 && (
        <Card data-testid="creator-cycle-section" style={{ marginBottom: '24px' }}><CardContent className="pt-5 pb-5">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: theme.colors.text.primary }}>Payout Cycle</h3>
            <select data-testid="creator-cycle-select" value={selectedCycle}
              onChange={e => setSelectedCycle(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borders}`, fontSize: '13px', background: '#FFF', color: theme.colors.text.primary }}>
              {cycles.map(c => (
                <option key={c.id} value={c.id}>{c.cycle_name} — {c.status}</option>
              ))}
            </select>
          </div>
          {cycleTotals && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '14px' }}>
              <div data-testid="cycle-total-gross"><span style={{ fontSize: '11px', color: theme.colors.text.muted }}>GROSS</span><div style={{ fontSize: '18px', fontWeight: 700, color: theme.colors.text.primary }}>${cycleTotals.gross_revenue.toFixed(2)}</div></div>
              <div data-testid="cycle-total-kickback"><span style={{ fontSize: '11px', color: theme.colors.text.muted }}>KICKBACK FUND</span><div style={{ fontSize: '18px', fontWeight: 700, color: '#10B981' }}>${cycleTotals.kickback_fund_usd.toFixed(2)}</div></div>
              <div data-testid="cycle-total-net"><span style={{ fontSize: '11px', color: theme.colors.text.muted }}>NET REVENUE</span><div style={{ fontSize: '18px', fontWeight: 700, color: theme.colors.text.primary }}>${cycleTotals.net_revenue_usd.toFixed(2)}</div></div>
              <div data-testid="cycle-total-units"><span style={{ fontSize: '11px', color: theme.colors.text.muted }}>UNITS</span><div style={{ fontSize: '18px', fontWeight: 700, color: theme.colors.text.primary }}>{cycleTotals.units_sold}</div></div>
            </div>
          )}
          {cycleFinancials.length === 0 ? (
            <p style={{ fontSize: '13px', color: theme.colors.text.muted, padding: '12px 0' }}>No financials for your portfolios in this cycle.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table data-testid="cycle-financials-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ borderBottom: `2px solid ${theme.colors.borders}` }}>
                  {['Portfolio','Gross','Units','Kickback %','Kickback Fund','Fees','Net'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: 600, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {cycleFinancials.map(f => (
                    <tr key={f.id} data-testid={`cycle-fin-row-${f.portfolio_id}`}
                      onClick={() => navigate(`/creator/portfolios/${f.portfolio_id}`)}
                      style={{ borderBottom: `1px solid ${theme.colors.borders}`, cursor: 'pointer' }}>
                      <td style={{ padding: '12px', fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>{f.portfolio_name}</td>
                      <td style={{ padding: '12px', fontSize: '13px', color: theme.colors.text.primary }}>${(f.gross_revenue||0).toFixed(2)}</td>
                      <td style={{ padding: '12px', fontSize: '13px', color: theme.colors.text.primary }}>{f.units_sold}</td>
                      <td style={{ padding: '12px', fontSize: '13px', color: theme.colors.text.primary }}>{f.kickback_percent}%</td>
                      <td style={{ padding: '12px', fontSize: '13px', fontWeight: 600, color: '#10B981' }}>${(f.kickback_fund_usd||0).toFixed(2)}</td>
                      <td style={{ padding: '12px', fontSize: '13px', color: theme.colors.text.muted }}>${(f.platform_fee_usd||f.fees_usd||0).toFixed(2)}</td>
                      <td style={{ padding: '12px', fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>${(f.net_to_creator_usd||f.net_revenue_usd||0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent></Card>
      )}

      {/* KPI Cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: theme.colors.text.muted }}>Loading performance data...</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '32px' }}>
            <Card><CardContent className="pt-5 pb-5">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: `${theme.colors.primary}14`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TrendingUp size={18} color={theme.colors.primary} />
                </div>
                <span style={{ fontSize: '13px', color: theme.colors.text.muted }}>Total Sales</span>
              </div>
              <p data-testid="kpi-total-sales" style={{ fontSize: '32px', fontWeight: 700, color: theme.colors.text.primary }}>{totals.total_sales}</p>
            </CardContent></Card>

            <Card><CardContent className="pt-5 pb-5">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <DollarSign size={18} color="#10B981" />
                </div>
                <span style={{ fontSize: '13px', color: theme.colors.text.muted }}>Gross Revenue</span>
              </div>
              <p data-testid="kpi-gross-revenue" style={{ fontSize: '32px', fontWeight: 700, color: theme.colors.text.primary }}>${totals.gross_revenue.toFixed(2)}</p>
            </CardContent></Card>

            <Card><CardContent className="pt-5 pb-5">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(99,102,241,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Package size={18} color="#6366F1" />
                </div>
                <span style={{ fontSize: '13px', color: theme.colors.text.muted }}>Units Sold</span>
              </div>
              <p data-testid="kpi-units-sold" style={{ fontSize: '32px', fontWeight: 700, color: theme.colors.text.primary }}>{totals.units_sold}</p>
            </CardContent></Card>
          </div>

          {/* Per-Portfolio Performance Table */}
          <Card><CardContent className="pt-5 pb-5">
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '16px' }}>Performance by Portfolio</h3>
            {perPortfolio.length === 0 ? (
              <p style={{ fontSize: '14px', color: theme.colors.text.muted, textAlign: 'center', padding: '24px' }}>No portfolios yet. Create one to start tracking performance.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table data-testid="performance-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${theme.colors.borders}` }}>
                      {['Portfolio', 'Sales', 'Revenue', 'Units Sold', 'Last Sale', ''].map((h, i) => (
                        <th key={i} style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: 600, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {perPortfolio.map((row, i) => (
                      <tr key={row.portfolio_id} data-testid={`perf-row-${row.portfolio_id}`}
                        onClick={() => navigate(`/creator/portfolios/${row.portfolio_id}`)}
                        style={{ borderBottom: `1px solid ${theme.colors.borders}`, cursor: 'pointer', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = theme.colors.bg.secondary}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '14px 12px', fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>{row.portfolio_name}</td>
                        <td style={{ padding: '14px 12px', fontSize: '14px', color: theme.colors.text.secondary }}>{row.sales_count}</td>
                        <td style={{ padding: '14px 12px', fontSize: '14px', fontWeight: 600, color: '#10B981' }}>${row.gross_revenue.toFixed(2)}</td>
                        <td style={{ padding: '14px 12px', fontSize: '14px', color: theme.colors.text.secondary }}>{row.units_sold}</td>
                        <td style={{ padding: '14px 12px', fontSize: '12px', color: theme.colors.text.muted }}>
                          {row.last_sale_at ? new Date(row.last_sale_at).toLocaleDateString() : '-'}
                        </td>
                        <td style={{ padding: '14px 12px' }}>
                          <ArrowRight size={14} color={theme.colors.text.muted} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent></Card>

          {/* Payout Configuration Section */}
          <Card style={{ marginTop: '20px' }}><CardContent className="pt-5 pb-5">
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '12px' }}>Recent Payouts</h3>
            {recentPayouts.length === 0 ? (
              <p style={{ fontSize: '14px', color: theme.colors.text.muted, textAlign: 'center', padding: '24px' }}>No payouts yet — wait for the next cycle to be calculated by an admin.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table data-testid="creator-payouts-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${theme.colors.borders}` }}>
                      {['Portfolio', 'Buyer', 'Amount', 'Status', ''].map((h, i) => (
                        <th key={i} style={{ textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: 600, color: theme.colors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentPayouts.map(p => (
                      <tr key={p.id} data-testid={`creator-payout-row-${p.id}`}
                        onClick={() => navigate(`/creator/payouts/${p.id}`)}
                        style={{ borderBottom: `1px solid ${theme.colors.borders}`, cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = theme.colors.bg.secondary}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '14px 12px', fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>{p.portfolio_name}</td>
                        <td style={{ padding: '14px 12px', fontSize: '12px', color: theme.colors.text.muted }}>{p.buyer_email_masked || (p.buyer_email ? p.buyer_email.replace(/(.{2}).*(@.*)/, '$1***$2') : '***')}</td>
                        <td style={{ padding: '14px 12px', fontSize: '14px', fontWeight: 600, color: '#10B981' }}>${Number(p.amount || 0).toFixed(2)}</td>
                        <td style={{ padding: '14px 12px', fontSize: '12px' }}>
                          <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: p.status === 'PAID' ? '#D1FAE5' : '#FEF3C7', color: p.status === 'PAID' ? '#059669' : '#D97706' }}>{p.status}</span>
                        </td>
                        <td style={{ padding: '14px 12px' }}>
                          <ArrowRight size={14} color={theme.colors.text.muted} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent></Card>

          {/* Payout Configuration Section */}
          <Card style={{ marginTop: '20px' }}><CardContent className="pt-5 pb-5">
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: theme.colors.text.primary, marginBottom: '12px' }}>Payout Configuration</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ padding: '16px', background: theme.colors.bg.secondary, borderRadius: theme.radius.md }}>
                <p style={{ fontSize: '12px', color: theme.colors.text.muted, marginBottom: '4px' }}>Payout Method</p>
                <p style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>Not configured</p>
              </div>
              <div style={{ padding: '16px', background: theme.colors.bg.secondary, borderRadius: theme.radius.md }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <Calendar size={12} color={theme.colors.text.muted} />
                  <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>Upcoming Cycle</p>
                </div>
                <p style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>Pending engine activation</p>
              </div>
            </div>
          </CardContent></Card>
        </>
      )}
    </div>
  );
};

export default CreatorPayoutsPageWorking;
